// ============================================================================
// CHANGO V5.0 — <RequireAuth> HOC (§9.6.2 路由守卫)
// ============================================================================
// Wraps any protected route element. Redirects to /login while preserving the
// originally requested URL in the `from` state so we can bounce the user back
// after a successful sign-in.
//
// §9.6.2d: 加 audit_flag / registration_status 路由守卫
//   - 当 registration_status === 'frozen_for_audit'：除 3 个白名单页路径外全部跳 /app/resubmit-license
//   - 当 audit_flag === 'not_started' 或 enabled_layers 为空：除 /app/welcome / /app/activate-trial / /app/account 外全部跳 /app/welcome
//
// Usage:
//   <Route path="/dashboard" element={<RequireAuth><Dashboard/></RequireAuth>}/>
// ============================================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth.js';

const WHITELIST_FROZEN_PATHS = ['/app/resubmit-license', '/app/settings'];

export default function RequireAuth({ children }) {
  const { isAuthenticated, isLoading, loginPath, workspace, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-chango-500 border-t-transparent" aria-hidden="true" />
          <span className="text-sm">加载中…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const from = location.pathname + location.search;
    return <Navigate to={loginPath || '/login'} replace state={{ from }} />;
  }

  // §9.6.2d 阶段 4：冻结后只能进 3 个白名单页
  if (workspace?.registration_status === 'frozen_for_audit') {
    const isWhitelisted = WHITELIST_FROZEN_PATHS.includes(location.pathname) ||
                         location.pathname.startsWith('/app/settings/');
    if (!isWhitelisted) {
      return <Navigate to="/app/resubmit-license" replace />;
    }
  }

  // §9.6.2 阶段 1 注册完但未激活：除了 /app/welcome / /app/activate-trial / /app/account 外全部跳 /app/welcome
  // audit_flag === 'not_started' 表示刚注册完还没选套餐激活
  if (
    workspace?.audit_flag === 'not_started' ||
    (workspace && (!workspace.enabled_layers || workspace.enabled_layers.length === 0))
  ) {
    const allowedPaths = ['/app/welcome', '/app/activate-trial', '/app/settings'];
    const isAllowed = allowedPaths.some(p => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/app/welcome" replace />;
    }
  }

  return children;
}
